export const GUEST_KITCHEN_KEY = 'agent_guest_kitchen_v1'
export const GUEST_TOKEN_KEY = 'agent_guest_token_v1'
export const HAS_ACCOUNT_KEY = 'agent_has_account'
export const GUEST_EXPIRED_KEY = 'agent_guest_expired_v1'
export const GUEST_RECOVERY_KEY = 'agent_guest_recovery_v1'
export const GUEST_KITCHEN_CHANGED_EVENT = 'agent:guest-kitchen-changed'
export const GUEST_SAVE_FAILED_EVENT = 'agent:guest-kitchen-save-failed'

/** Bumps on every local write so a slower server save cannot restore an older kitchen. */
let localRevision = 0
/** Saves run one at a time. Each request sends the kitchen as it is when that request starts. */
let syncTail = Promise.resolve()

export function resetGuestKitchenSyncForTests() {
  localRevision = 0
  syncTail = Promise.resolve()
}

export const GUEST_TTL_MS = 24 * 60 * 60 * 1000

function safeParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function createEmptyGuestKitchen(createdAt = new Date().toISOString()) {
  return {
    version: 1,
    createdAt,
    pantry: [],
    recipes: [],
    preferences: {},
    planMealNames: [],
    onboardingStep: 1,
    onboardingComplete: false,
    onboardingPlanInitialized: false,
    keepShown: false,
    keepDismissed: false,
  }
}

/** The save prompt was offered. Setup stays open so the week underneath does not change. */
export function markOnboardingSaveOffered(kitchen) {
  if (!kitchen || kitchen.keepShown) return kitchen
  return { ...kitchen, keepShown: true }
}

/** Leave setup for the full planner. Saving an account does this by finishing the guest kitchen. */
export function completeGuestOnboarding(kitchen) {
  if (!kitchen) return kitchen
  return {
    ...kitchen,
    onboardingStep: 4,
    onboardingComplete: true,
    keepShown: true,
  }
}

export function isGuestKitchenExpired(kitchen, now = Date.now()) {
  const expiresAt = Date.parse(kitchen?.expiresAt || '')
  const createdAt = Date.parse(kitchen?.createdAt || '')
  const deadline = Number.isFinite(expiresAt) ? expiresAt : createdAt + GUEST_TTL_MS
  return !Number.isFinite(deadline) || deadline <= now
}

export function clearGuestKitchen({ expired = false } = {}) {
  localRevision += 1
  try {
    const current = localStorage.getItem(GUEST_KITCHEN_KEY)
    if (expired && current) sessionStorage.setItem(GUEST_RECOVERY_KEY, current)
    localStorage.removeItem(GUEST_KITCHEN_KEY)
    localStorage.removeItem(GUEST_TOKEN_KEY)
    if (expired) sessionStorage.setItem(GUEST_EXPIRED_KEY, '1')
  } catch {
    // Storage may be unavailable; callers still update in-memory state.
  }
  window.dispatchEvent(new CustomEvent(GUEST_KITCHEN_CHANGED_EVENT, { detail: { kitchen: null } }))
}

export function readGuestKitchen({ allowExpired = false } = {}) {
  let kitchen = null
  try {
    kitchen = safeParse(localStorage.getItem(GUEST_KITCHEN_KEY))
  } catch {
    return null
  }
  if (!kitchen || kitchen.version !== 1) {
    if (kitchen) clearGuestKitchen()
    return null
  }
  if (!allowExpired && isGuestKitchenExpired(kitchen)) {
    clearGuestKitchen({ expired: true })
    return null
  }
  return kitchen
}

export function hasExpiredGuestMarker() {
  try {
    return sessionStorage.getItem(GUEST_EXPIRED_KEY) === '1'
  } catch {
    return false
  }
}

export function clearExpiredGuestMarker() {
  try {
    sessionStorage.removeItem(GUEST_EXPIRED_KEY)
  } catch {
    // ignore
  }
}

export function discardExpiredGuestRecovery() {
  try {
    sessionStorage.removeItem(GUEST_RECOVERY_KEY)
    sessionStorage.removeItem(GUEST_EXPIRED_KEY)
  } catch {
    // Ignore unavailable storage.
  }
}

export function writeGuestKitchen(kitchen) {
  localRevision += 1
  const next = { ...createEmptyGuestKitchen(kitchen?.createdAt), ...kitchen, version: 1 }
  localStorage.setItem(GUEST_KITCHEN_KEY, JSON.stringify(next))
  window.dispatchEvent(new CustomEvent(GUEST_KITCHEN_CHANGED_EVENT, { detail: { kitchen: next } }))
  return next
}

export function updateGuestKitchen(updater) {
  const current = readGuestKitchen()
  if (!current) return null
  const next = typeof updater === 'function' ? updater(current) : { ...current, ...updater }
  return writeGuestKitchen(next)
}

export function readGuestToken() {
  try {
    return localStorage.getItem(GUEST_TOKEN_KEY)
  } catch {
    return null
  }
}

export async function startGuestKitchen() {
  const recovery = safeParse(sessionStorage.getItem(GUEST_RECOVERY_KEY))
  const response = await fetch('/guest/start', { method: 'POST' })
  const data = await response.json().catch(() => ({}))
  if (!response.ok || !data.guestToken || !data.kitchen) {
    throw new Error(data.detail || 'Could not start your kitchen')
  }
  localStorage.setItem(GUEST_TOKEN_KEY, data.guestToken)
  clearExpiredGuestMarker()
  let kitchen = writeGuestKitchen({
    ...data.kitchen,
    ...(recovery?.version === 1 ? recovery : {}),
    createdAt: data.kitchen.createdAt,
    expiresAt: data.expiresAt,
  })
  if (recovery?.version === 1) {
    kitchen = await syncGuestKitchen(kitchen)
    sessionStorage.removeItem(GUEST_RECOVERY_KEY)
  }
  return kitchen
}

async function putGuestKitchen(kitchen) {
  const token = readGuestToken()
  if (!kitchen || !token) throw new Error('Guest kitchen is unavailable')
  const response = await fetch('/guest/kitchen', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Guest-Token': token,
    },
    body: JSON.stringify(kitchen),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error = new Error(data.detail || 'Could not save your last change')
    error.status = response.status
    throw error
  }
  return data
}

export async function syncGuestKitchen(kitchen = readGuestKitchen()) {
  const run = syncTail.then(async () => {
    const payload = readGuestKitchen() || kitchen
    if (!payload) throw new Error('Guest kitchen is unavailable')
    const revisionAtSend = localRevision
    try {
      const data = await putGuestKitchen(payload)
      if (localRevision !== revisionAtSend) return readGuestKitchen()
      return writeGuestKitchen({ ...data.kitchen, expiresAt: data.expiresAt })
    } catch (error) {
      const stillCurrent = localRevision === revisionAtSend
      if (stillCurrent && error?.status === 410) clearGuestKitchen({ expired: true })
      if (stillCurrent) {
        window.dispatchEvent(new CustomEvent(GUEST_SAVE_FAILED_EVENT, { detail: { kitchen: payload } }))
        throw error
      }
      return readGuestKitchen()
    }
  })
  syncTail = run.then(() => {}, () => {})
  return run
}

export async function saveGuestKitchen(updater) {
  const kitchen = updateGuestKitchen(updater)
  if (!kitchen) throw new Error('Guest kitchen is unavailable')
  await syncGuestKitchen(kitchen)
  return kitchen
}

export async function mergeGuestKitchen() {
  const token = readGuestToken()
  if (!token) throw new Error('Guest kitchen is unavailable')
  const response = await fetch('/guest/merge', {
    method: 'POST',
    headers: { 'X-Guest-Token': token, ...getAuthHeader() },
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(data.detail || 'Could not attach your kitchen')
  return data
}

export function discardGuestKitchen() {
  const token = readGuestToken()
  clearGuestKitchen()
  if (!token) return Promise.resolve()
  return fetch('/guest/kitchen', {
    method: 'DELETE',
    headers: { 'X-Guest-Token': token },
  }).then((response) => {
    if (!response.ok && response.status !== 404 && response.status !== 410) {
      throw new Error('Could not remove the guest kitchen from the server')
    }
  })
}

function getAuthHeader() {
  let token = null
  try {
    token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token')
  } catch {
    // ignore
  }
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export function getOnboardingProfileNames(preferences) {
  const names = []
  const firstName = String(preferences?.firstName || '').trim()
  if (firstName) names.push(firstName)
  const members = preferences?.family_members
  if (members && typeof members === 'object') {
    for (const [key, value] of Object.entries(members)) {
      if (key === 'you') continue
      const name = String(value?.name || '').trim()
      if (name && !names.some((existing) => existing.toLowerCase() === name.toLowerCase())) {
        names.push(name)
      }
    }
  }
  return names
}

export const ONBOARDING_RECIPES_TO_CONTINUE = 3

export function onboardingRecipeProgressLabel(recipeCount = 0, required = ONBOARDING_RECIPES_TO_CONTINUE) {
  const count = Math.max(0, Number(recipeCount) || 0)
  if (count >= required) return ''
  return 'Add three recipes to continue.'
}

export function buildOnboardingStepDetails({ pantryCount = 0, recipeCount = 0, profileNames = [] } = {}) {
  return [
    pantryCount > 0 ? `${pantryCount} ${pantryCount === 1 ? 'item' : 'items'}` : '',
    recipeCount > 0 ? `${recipeCount} ${recipeCount === 1 ? 'recipe' : 'recipes'}` : '',
    profileNames.length ? profileNames.join(', ') : '',
    '',
  ]
}

export function getKeepInventory(kitchen = readGuestKitchen()) {
  const pantryCount = Array.isArray(kitchen?.pantry) ? kitchen.pantry.length : 0
  const recipeCount = Array.isArray(kitchen?.recipes) ? kitchen.recipes.length : 0
  const plannedDinners = Array.isArray(kitchen?.planMealNames) ? kitchen.planMealNames.length : 0
  const preferencesSaved = Boolean(
    kitchen?.preferences && Object.keys(kitchen.preferences).length > 0,
  )
  const profileNames = getOnboardingProfileNames(kitchen?.preferences)
  return { pantryCount, recipeCount, plannedDinners, preferencesSaved, profileNames }
}

export function shouldAutoShowKeep(kitchen = readGuestKitchen()) {
  if (
    !kitchen
    || kitchen.onboardingComplete !== true
    || kitchen.keepShown
    || kitchen.keepDismissed
  ) return false
  const inventory = getKeepInventory(kitchen)
  const hasPackOrImport = (kitchen.recipes || []).some(
    (recipe) => recipe?.source === 'pack' || recipe?.source === 'import',
  )
  return inventory.pantryCount >= 10 && hasPackOrImport
}

export function markHasAccount() {
  try {
    localStorage.setItem(HAS_ACCOUNT_KEY, '1')
  } catch {
    // ignore
  }
}

export function hasAccountHint() {
  try {
    return localStorage.getItem(HAS_ACCOUNT_KEY) === '1'
  } catch {
    return false
  }
}
